import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Disclaimer } from "@/components/shared";
import { Send, Plus, MessageCircleHeart, Sparkles, Copy, Check, ListPlus, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { ChatMarkdown } from "@/components/ChatMarkdown";
import { EmailDraftDialog } from "@/components/EmailDraftDialog";
import { toast } from "sonner";

const stripMd = (s) => (s || "").replace(/[#*_>`]/g, "").replace(/^\s*[-•]\s*/gm, "").replace(/\n+/g, " ").trim();

function MessageActions({ content }) {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy to clipboard."); }
  };
  const saveTask = async () => {
    setSaving(true);
    try {
      const title = stripMd(content).slice(0, 80) || "KidPath note";
      await api.post("/tasks", { title, description: content, category: "general_next_step" });
      toast.success("Saved to your tasks.", { action: { label: "View", onClick: () => window.location.assign("/app/tasks") } });
    } catch (e) {
      if (e.response?.status === 402) toast.error("Saving to tasks is a Family Path feature. Upgrade to unlock.");
      else toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save as task.");
    } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-1 mt-2 pt-2 border-t border-kid-line/70" data-testid="ai-message-actions">
      <button onClick={copy} className="inline-flex items-center gap-1 text-xs font-poppins text-kid-muted hover:text-kid-teal px-2 py-1 rounded-lg hover:bg-white transition-colors" data-testid="ai-copy">
        {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
      </button>
      <button onClick={saveTask} disabled={saving} className="inline-flex items-center gap-1 text-xs font-poppins text-kid-muted hover:text-kid-teal px-2 py-1 rounded-lg hover:bg-white transition-colors disabled:opacity-60" data-testid="ai-save-task">
        <ListPlus size={13} /> {saving ? "Saving…" : "Save as task"}
      </button>
    </div>
  );
}

const SUGGESTIONS = [
  "What should I do first?",
  "What documents should I gather?",
  "Explain the Disability Tax Credit.",
  "Help me prepare for a school meeting.",
  "Draft a calm email to the school.",
];

export default function AiNavigator() {
  const { isPaid } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const scrollRef = useRef(null);

  const loadConvs = async () => {
    const { data } = await api.get("/ai/conversations");
    setConversations(data);
    return data;
  };
  useEffect(() => { loadConvs(); }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const openConv = async (id) => {
    const { data } = await api.get(`/ai/conversations/${id}`);
    setActive(data.conversation);
    setMessages(data.messages);
  };

  const newConv = async () => {
    const { data } = await api.post("/ai/conversations", { title: "New conversation" });
    setActive(data);
    setMessages([]);
    loadConvs();
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    let conv = active;
    if (!conv) { const { data } = await api.post("/ai/conversations", { title: "New conversation" }); conv = data; setActive(data); }
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content }]);
    setSending(true);
    try {
      const { data } = await api.post(`/ai/conversations/${conv.id}/messages`, { content });
      setMessages((m) => [...m, data.message]);
      loadConvs();
    } catch (e) {
      if (e.response?.status === 429) { setLimitHit(true); }
      else { setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", content: formatApiError(e.response?.data?.detail) || "The AI Family Navigator is unavailable right now. Please try again shortly." }]); }
    } finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="AI Family Navigator" title="Ask KidPath" subtitle="Plain-language help to understand programs, prepare questions, and draft calm messages." />

      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        {/* Conversations */}
        <div className="hidden lg:flex flex-col">
          <button onClick={newConv} className="kid-btn kid-btn-ghost h-11 mb-3" data-testid="ai-new-conversation"><Plus size={16} /> New conversation</button>
          <div className="space-y-1.5 overflow-y-auto">
            {conversations.map((c) => (
              <button key={c.id} onClick={() => openConv(c.id)} data-testid="ai-conversation-item"
                className={`w-full text-left px-3 py-2.5 rounded-xl text-sm truncate transition-colors ${active?.id === c.id ? "bg-kid-teal text-white" : "bg-white text-kid-ink hover:bg-kid-skytint"}`}>
                {c.title}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="kid-card flex flex-col h-[560px]" data-testid="ai-thread">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && !sending && (
              <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="ai-empty">
                <div className="w-14 h-14 rounded-2xl bg-kid-grape/12 text-kid-grape grid place-items-center mb-4"><MessageCircleHeart size={26} /></div>
                <h3 className="font-bold text-kid-navy text-lg">No conversations yet</h3>
                <p className="text-kid-muted text-sm mt-1 mb-5 max-w-sm">Ask KidPath to explain a program, prepare a checklist, or draft a calm school email.</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="px-3.5 py-2 rounded-full text-sm bg-kid-skytint text-kid-teal font-poppins hover:bg-kid-teal hover:text-white transition-colors" data-testid="ai-suggestion">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-message-${m.role}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-kid-teal text-white whitespace-pre-wrap" : "bg-kid-mist text-kid-ink border border-kid-line"}`}>
                  {m.role === "user" ? m.content : <ChatMarkdown>{m.content}</ChatMarkdown>}
                  {m.role === "assistant" && !String(m.id).startsWith("err-") && <MessageActions content={m.content} />}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="ai-loading">
                <div className="bg-kid-mist border border-kid-line rounded-2xl px-4 py-3 text-sm text-kid-muted">KidPath is preparing a response…</div>
              </div>
            )}
          </div>

          {limitHit ? (
            <div className="border-t border-kid-line p-4 text-center" data-testid="ai-limit">
              <p className="text-sm text-kid-navy mb-2">You've reached your AI question limit for this plan.</p>
              {!isPaid && <Link to="/app/account" className="kid-btn kid-btn-primary h-10 px-5 inline-flex"><Sparkles size={15} /> Upgrade to Family Path</Link>}
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-kid-line p-3" data-testid="ai-input-form">
              <div className="flex items-center gap-2 mb-2">
                <button type="button" onClick={() => setEmailOpen(true)} disabled={sending}
                  className="inline-flex items-center gap-1.5 text-xs font-poppins font-semibold text-kid-teal bg-kid-skytint hover:bg-kid-teal hover:text-white px-3 py-1.5 rounded-full transition-colors disabled:opacity-60" data-testid="ai-draft-email">
                  <Mail size={14} /> Draft a school email
                </button>
              </div>
              <div className="flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} disabled={sending}
                       placeholder="Ask a question…" className="flex-1 rounded-xl border border-kid-line px-4 h-11 text-sm focus:outline-none focus:border-kid-teal" data-testid="ai-input" />
                <button type="submit" disabled={sending || !input.trim()} className="kid-btn kid-btn-primary h-11 px-5 disabled:opacity-50" data-testid="ai-send"><Send size={16} /></button>
              </div>
            </form>
          )}
        </div>
      </div>

      <EmailDraftDialog open={emailOpen} onClose={() => setEmailOpen(false)} onSubmit={(prompt) => { setEmailOpen(false); send(prompt); }} />

      <div className="mt-5">
        <Disclaimer>The AI Family Navigator can help explain, organize, summarize, and prepare questions. It does not provide medical, legal, tax, or eligibility advice.</Disclaimer>
      </div>
    </div>
  );
}
